import { CommonModule } from '@angular/common';
import { Component, Input } from '@angular/core';

import { IonicModule, IonModal, ModalController } from '@ionic/angular';

import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';


import { DocumentService } from '@core/services/document/document.service';
import { LoadingService } from '@core/services/loading/loading.service';
import { NavigationService } from '@core/services/navigation/navigation.service';
import { AccountVisibilityService } from '@core/services/products/account-visibility.service';
import { ProductsService } from '@core/services/products/products.service';

// Removed modal result flow – inline message is used instead

import { Product } from '@shared/models/products.model';

import { SharedModule } from '@shared/shared.module';

@UntilDestroy()
@Component({
    selector: 'app-disable-parameter-changes-modal',
    templateUrl: './disable-parameter-changes-modal.component.html',
    styleUrls: ['./disable-parameter-changes-modal.component.scss'],
    imports: [CommonModule, IonicModule, SharedModule]
})
export class DisableParameterChangesModalComponent {
  @Input() modal: IonModal;
  @Input() staticHeight: boolean;
  @Input() product: Product;

  @Input() isCompleted = false; // завершено выполнение операции
  @Input() isSuccess = false;   // успешный результат
  @Input() errorText?: string;  // текст ошибки для отображения

  constructor(
    private loadingService: LoadingService,
    private accountVisibilityService: AccountVisibilityService,
    private navigationService: NavigationService,
    private modalController: ModalController,
    private readonly documentService: DocumentService,
    private readonly productsService: ProductsService
  ) {}

  /**
   * Форматирует номер счета для отображения
   */
  protected get formattedAccountNumber(): string {
    const accountNumber = this.product.number || this.product.id;

    if (!accountNumber || accountNumber.length !== 20) {
      return accountNumber;
    }

    // Форматируем как: 40 817 810 202 050 000 383
    return accountNumber
      .replace(/(\d{2})(\d{3})(\d{3})(\d{3})(\d{3})(\d{3})(\d{3})/, '$1 $2 $3 $4 $5 $6 $7')
      .replace(/ /g, '\u00A0');
  }

  /**
   * Выполняет запрет изменения параметров
   */
  protected disableParameterChanges(): void {
    this.loadingService.showLoader(true);

    // Используем AccountVisibilityService для установки запрета на изменения
    // true - признак счета, false - признак карты
    this.accountVisibilityService
      .forbidChange(this.product.id, true)
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (response) => {
          this.loadingService.showLoader(false);
          console.log('Запрет изменений установлен успешно:', response);
          // Меняем состояние интерфейса: показываем сообщение, скрываем кнопку
          this.isCompleted = true;
          this.isSuccess = true;

          // Запускаем polling статуса документа в фоне
          // Документы product_restriction используют docModule='ibankfl', docType='product_restriction'
          this.waitForDocumentProcessing(response.id);
        },
        error: (error) => {
          this.loadingService.showLoader(false);
          console.error('Ошибка установки запрета изменений:', error);
          // Меняем состояние интерфейса: показываем ошибку, скрываем кнопку
          this.isCompleted = true;
          this.isSuccess = false;
          this.errorText = error?.userMessage || 'Произошла ошибка при установке запрета';
        }
      });
  }

  /**
   * Polling статуса документа через DocumentService.getDocWithStatus.
   * Когда документ обработан (status === 'end'), обновляем кэш и делаем refresh.
   */
  private waitForDocumentProcessing(docId: string): void {
    this.documentService
      .getDocWithStatus<Record<string, unknown>>(
        'ibankfl',
        'product_restriction',
        docId,
        30,
        ['end', 'decline', 'cancel']
      )
      .pipe(untilDestroyed(this))
      .subscribe({
        next: (doc) => {
          const docStatus = doc?.['status']?.toString().toLowerCase();

          if (docStatus === 'end' && this.product?.id) {
            this.productsService.updateProductField(this.product.id, 'forbiddenChange', 'true');
            this.productsService.refreshProducts$.next(true);
          }
        },
        error: (error) => {
          console.warn('Document polling error (proceeding with optimistic update):', error);
        }
      });
  }

  /**
   * Закрывает модальное окно и переходит на главную страницу
   */
  protected async goToMain(): Promise<void> {
    await this.modalController.dismiss();

    // Оптимистичное обновление: устанавливаем forbiddenChange в кэше немедленно
    if (this.product?.id) {
      this.productsService.updateProductField(this.product.id, 'forbiddenChange', 'true');
    }

    await this.navigationService.setRoot('/main');
  }
}
